<template>
    <InputDropdown
        ref="inputDropdown"
        :items="items"
        :item-label="getLabel"
        :item-value="getValue"
        :error="error"
        :disabled="disabled"
        :multiple="multiple"
        :options-loading="optionsLoading"
        key-field="countryCode"
        v-bind="bindings"
        @update:model-value="emit('update:modelValue', $event)"
        @clear="emit('clear')"
        @dropdown-open="onDropdownOpen"
    >
        <template #dropdown-icon>
            <LoadingCircle v-if="optionsLoading" />
        </template>

        <template #dropdown-selected-option="{ selectedItemLabel, selectedItemValue }">
            <div
                v-if="selectedItemValue"
                class="option-container"
            >
                <img
                    v-if="displayCountryFlags && selectedItemValue && !multiple"
                    :src="`https://flagcdn.com/w40/${selectedItemValue?.toLowerCase()}.webp`"
                    :srcset="`https://flagcdn.com/w80/${selectedItemValue?.toLowerCase()}.webp 2x`"
                    @error="onFlagLoadError"
                />
                <Words
                    oneline
                    :class="{ 'text-break': selectedItemValue }"
                >{{ selectedItemLabel }}</Words>
            </div>
        </template>

        <template #dropdown-option="{ item }">
            <div class="option-container">
                <img
                    v-if="displayCountryFlags"
                    :src="`https://flagcdn.com/w40/${getValue(item)?.toLowerCase()}.webp`"
                    :srcset="`https://flagcdn.com/w80/${getValue(item)?.toLowerCase()}.webp 2x`"
                    @error="onFlagLoadError"
                />
                <Words
                    oneline
                    class="text-break"
                >{{ getLabel(item) }}</Words>
            </div>
        </template>
    </InputDropdown>
</template>

<script setup lang="ts">
import { omit } from 'ramda'
import { computed, inject, ref } from 'vue'
import InputDropdown from 'widget-components/components/InputDropdown/InputDropdown.vue'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import type { BlockPath, InputCountry, InputCountryItem, InputDropdownItem, LazyBlockToolsInject } from 'widget-components/integrations/CustomPage/types'
import LoadingCircle from '../LoadingCircle/LoadingCircle.vue'
import type { InputDropdownProps } from '../InputDropdown/types'
import Words from 'widget-components/components/Words/Words.vue'
import { PALE_PLACEHOLDER_IMAGE_URL } from 'widget-components/utilities/placeholders'

const getLabel = (item: InputCountryItem) => item.label!
const getValue = (item: InputCountryItem) => item.countryCode!

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id: string
    modelValue?: InputDropdownItem | InputDropdownItem[]
    initialValue?: InputDropdownItem | InputDropdownItem[]
    error?: string | boolean
    disabled?: boolean
    items?: InputDropdownItem[]
    selectedItems?: InputDropdownItem[]
    label?: string
    multiple?: boolean
    displayCountryFlags?: boolean
    showChips?: boolean
    itemLabel?: () => string
    itemValue?: () => InputDropdownItem
    blockPath?: BlockPath
    placeholder?: string
}>()

const inputDropdown = ref<InstanceType<typeof InputDropdown>>()

const {
    requestBlockConfig,
    getLazyBlockProp,
} = inject(lazyBlockToolsInjectionKey, {
    requestBlockConfig: () => {},
    getLazyBlockProp: () => {} ,
    dispatchComponentAction: async () => {},
} as LazyBlockToolsInject)

const error = computed(() => props.error || getLazyBlockProp<string>(props.id, 'error'))

const optionsLoading = computed(() => getLazyBlockProp<boolean>(props.id, 'loading'))

const items = computed(() => {
    const lazyItems = getLazyBlockProp<InputCountry[] | undefined>(props.id || 'no-id', 'items')

    if (lazyItems?.length) {
        return lazyItems
    }

    if (props.selectedItems?.length) {
        return props.selectedItems
    }

    return props.items || []
})

const emit = defineEmits(['update:modelValue', 'clear'])

const bindings = computed<Partial<InputDropdownProps>>(() => ({
    ...omit(['items', 'error', 'disabled', 'selectedItems', 'displayCountryFlags', 'multiple', 'itemLabel', 'itemValue'], props),
}))

const onDropdownOpen = async () => {
    await requestBlockConfig({ id: props.id || 'no-id', type: 'INPUT_COUNTRY' })
    inputDropdown.value?.updateDropdownOverlay()
    inputDropdown.value?.focusSearch()
}

const onFlagLoadError = (event: Event) => {
    const imgElement = event.target as HTMLImageElement
    imgElement.src = PALE_PLACEHOLDER_IMAGE_URL
    imgElement.removeAttribute('srcset')
}

</script>

<style lang="sass" scoped>
@import './InputCountry.sass'
</style>
